import React, { useEffect, useState } from "react";
import { Map, List } from "immutable";
import { isEqual } from "lodash";
import { Text, Checkbox, Box } from "@showitinc/showit-wc";

import AppConstants from "../constants/AppConstants";
import CanvasSetUpdate from "../vo/CanvasSetUpdate";
import UIActions from "../actions/UIActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import When from "./utils/When";
import NameSlugInput from "./NameSlugInput";
import PropertyPanelSectionWrapper from "../app2components/base/PropertyPanelSectionWrapper";

const { UPDATE_ACTION_SET, ITEM_TYPE_CANVAS_SET } = AppConstants;

/**
 * @typedef {string} pageId
 */

/**
 * Returns a list of pages using a canvasSet as a list of pageIds
 * @param {blockContainer} blockContainer
 * @param {string} canvasSetId
 * @returns {List<pageId>} - List of pageIds
 */
const getPageList = (blockContainer, canvasSetId) =>
  blockContainer
    .getIn(["data", "items"])
    .reduce(
      (result, item) =>
        item.get("canvasSet", List()).indexOf(canvasSetId) !== -1
          ? result.push(item.get("id"))
          : result,
      List()
    );

/**
 * Returns a map of all pages as key with a boolean as value indicating whether or not a page is using a canvasSet
 * @param {blockContainer} blockContainer
 * @param {List<pageId>} pageList - List of pageIds using a canvasSet
 * @returns {Map<pageId, boolean>} - Map of pages and canvasSet inclusion
 */
const getPageState = (blockContainer, pageList) =>
  blockContainer.getIn(["data", "items"]).reduce((result, page) => {
    const pageId = page.get("id");
    return result.set(pageId, pageList.indexOf(pageId) !== -1);
  }, Map());

/**
 * Returns a List of pageIds from pageState if the page is using a canvasSet
 * @param {Map<pageId, boolean>} pageState
 * @returns {List<pageId>} - List of pageIds using a canvasSet
 */
const getPageListFromState = (pageState) =>
  pageState.reduce((result, value, pageId) => (value ? result.push(pageId) : result), List());

/**
 * Creates propPath for updating a page item with using CanvasSetUpdate
 * @param {string} pageId
 * @returns {string} - PropPath to update the canvasSet list for a page item
 */
const canvasSetPath = (pageId) => `items.${pageId}.canvasSet`;

/**
 * Returns a callback that will open the rename panel for a configured canvas set
 * @param {string} id
 * @param {string} name
 * @param {string} slug
 * @returns {() => void}
 */
const toggleRenamePopup = (id, name, slug) => () => {
  UIActions.toggleRenamePopup({
    title: "Canvas Set",
    itemType: ITEM_TYPE_CANVAS_SET,
    itemId: id,
    name,
    slug,
    showWarning: false,
  });
};

const CanvasSetProperties = ({ blockContainer }) => {
  const id = blockContainer.get("id");
  const name = blockContainer.get("name");
  const slug = blockContainer.get("slug");
  const pageListOrder = blockContainer.getIn(["data", "pages"], List());

  const [pageList, setPageList] = useState(getPageList(blockContainer, id));
  const [pageState, setPageState] = useState(getPageState(blockContainer, pageList));
  const [updates, setUpdates] = useState(new CanvasSetUpdate(id));

  const allSelected = pageState.every((val) => val);

  const resetPageState = () => setPageState(getPageState(blockContainer, pageList));
  const resetUpdates = () => setUpdates(new CanvasSetUpdate(id));
  const reset = () => {
    resetPageState();
    resetUpdates();
  };

  const addCanvasSetUpdate = (update) => {
    const { name: propPath, value } = update;
    setUpdates((updater) => {
      const newValue = value ? [id] : [];
      updater.add({ ...update, propPath, value: newValue, action: UPDATE_ACTION_SET });
      return updater;
    });
  };

  const filterCanvasSetUpdate = (propPath) => {
    const splitPath = propPath.split(".");
    setUpdates((prevUpdates) => {
      const { updates: updateList } = prevUpdates;
      const updater = new CanvasSetUpdate(id);
      updater.updates = updateList.filter(
        (currentUpdate) => !isEqual(currentUpdate.key, splitPath)
      );
      return updater;
    });
  };

  // for programmatic add and remove
  const formatUpdate = (pageId, value) => ({
    propPath: canvasSetPath(pageId),
    value: value ? [id] : [],
    action: UPDATE_ACTION_SET,
    addUndo: false,
    source: "toggleAll",
  });

  const onPageToggle = (pageId, update) => {
    const { value, name: propPath } = update;
    const splitPath = propPath.split(".");
    setPageState(pageState.set(pageId, value));
    const updateExists = updates.updates.filter(({ key }) => isEqual(key, splitPath)).length;

    updateExists ? filterCanvasSetUpdate(propPath) : addCanvasSetUpdate(update);
  };

  const onToggleAll = () => {
    resetPageState();
    setUpdates(() => {
      const updater = new CanvasSetUpdate(id);
      const originalState = getPageState(blockContainer, pageList);

      setPageState((prevPageState) =>
        prevPageState.reduce((result, _, pageId) => {
          const value = !allSelected;
          // send update with value if original page map item does not have that item
          // unset originally set pages, or set originally unset pages
          originalState.get(pageId) !== value && updater.add(formatUpdate(pageId, value));
          return result.set(pageId, value);
        }, Map())
      );
      return updater;
    });
  };

  const onSaveUpdates = () => {
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_CANVAS_SET, payload: updates });
    resetUpdates();
  };

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // reset list/page state when a new list deps are changes within the blockContainer
    const newPageList = getPageList(blockContainer, id);
    if (!pageList.equals(newPageList)) {
      setPageList(() => {
        setPageState(getPageState(blockContainer, newPageList));
        return newPageList;
      });
    }
  }, [blockContainer, id, pageList, setPageList]);

  const [pages, wpPages] = pageListOrder.reduce(
    (result, pageId) => {
      const page = blockContainer.getIn(["data", "items", pageId]);
      const pageName = page.get("name");
      const checked = pageState.get(pageId);

      const index = page.get("wp") === undefined ? 0 : 1;

      result[index].push(
        <Checkbox
          key={pageId}
          label={pageName}
          checked={checked}
          onSIChange={(e) =>
            onPageToggle(pageId, {
              addUndo: false,
              name: canvasSetPath(pageId),
              source: "propertyInput",
              value: e.target.checked,
            })
          }
        />
      );

      return result;
    },
    [[], []]
  );

  return (
    <div style={{ overflowY: "scroll" }}>
      <PropertyPanelSectionWrapper title="Set Properties" open>
        <NameSlugInput
          propName="custom"
          name={name}
          slug={slug}
          hideSlug
          labelPrefix=""
          onAreaFocus={toggleRenamePopup(id, name, slug)}
        />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Checkbox
            key={`${id}-addToAllPages`}
            type="checkbox"
            label="Apply to all pages"
            checked={allSelected}
            onSIChange={onToggleAll}
          />
          <When it={pages.length}>
            <Text className="panel-subheading">Pages</Text>
            {pages}
          </When>
          <When it={wpPages.length}>
            <Text className="panel-subheading">Blog Templates</Text>
            {wpPages}
          </When>
        </div>

        {!getPageListFromState(pageState).equals(pageList) && updates.updates.length ? (
          <Box ml="3" mt="3">
            <button className="si-c-btn si-c-btn--primary" type="button" onClick={onSaveUpdates}>
              Save
            </button>
          </Box>
        ) : null}
      </PropertyPanelSectionWrapper>
    </div>
  );
};

export default CanvasSetProperties;
